import { generatePath, Link } from "react-router-dom";

import { cn } from "@/libs/cn";
import { GridTableCell, Tag, Tooltip } from "@/modules/common/components";
import {
  MANAGER_USER_ROLE_OPTIONS,
  MANAGER_USER_STATUS_OPTIONS,
  ManagerUserRole,
  ManagerUserStatus,
} from "@/modules/common/types/ManagerUsers.enum";
import { formatDate, formatNameInitials, formatValue } from "@/modules/common/utils/format";
import { ROUTES } from "@/routes/constants/routes";
import { AdminManagementCompanyManagerUserResponse } from "@/service/response/admin-management-companies";

import { gridLayout } from "./ManagerUsersTabListHeader";

interface ManagerUsersTabListLineProps {
  managementCompanyId: string;
  user: AdminManagementCompanyManagerUserResponse;
}

const ManagerUsersTabListLine = ({ managementCompanyId, user }: ManagerUsersTabListLineProps) => {
  const isActive = user.status === ManagerUserStatus.ACTIVE;
  const isAdmin = user.role === ManagerUserRole.ADMIN;
  const statusLabel = MANAGER_USER_STATUS_OPTIONS.find((option) => option.value === String(user.status))?.label ?? "-";
  const roleLabel = MANAGER_USER_ROLE_OPTIONS.find((option) => option.value === String(user.role))?.label ?? "-";

  const rowClassName = cn(
    gridLayout,
    "bg-white border border-[#D6DEE9] md:rounded-none md:border-0 md:border-t md:bg-transparent rounded-lg",
    "hover:bg-ui-hover transition-colors",
  );

  return (
    <Link
      to={generatePath(ROUTES.MANAGER.MANAGER_USER_DETAILS, { id: managementCompanyId, managerUserId: user.id })}
      className={rowClassName}
    >
      <GridTableCell label="Nome" className="md:min-w-0">
        <div className="flex items-center gap-3 md:min-w-0">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-800/10 text-sm font-semibold text-blue-800">
            {formatNameInitials(user.name)}
          </div>
          <div className="flex min-w-0 flex-col">
            <div className="flex min-w-0 items-center gap-2">
              <Tooltip label={user.name}>
                <span className="block truncate text-sm font-medium text-ui-navy-dark">{user.name}</span>
              </Tooltip>
              {user.isOwner && <Tag label="Representante" size="small" className="shrink-0 py-0" />}
            </div>
            <span className="block truncate text-xs text-ui-text-muted">
              {user.phoneNumber ? formatValue("phoneNumber", user.phoneNumber) : "-"}
            </span>
          </div>
        </div>
      </GridTableCell>

      <GridTableCell label="E-mail" className="text-sm text-ui-navy-dark md:min-w-0">
        <Tooltip label={user.email}>
          <span className="block truncate">{user.email}</span>
        </Tooltip>
      </GridTableCell>

      <GridTableCell label="Perfil" className="text-sm text-ui-navy-dark md:min-w-0">
        <Tag label={roleLabel} variant={isAdmin ? "primary" : "neutral"} />
      </GridTableCell>

      <GridTableCell label="Status">
        <Tag label={statusLabel} variant={isActive ? "success" : "neutral"} />
      </GridTableCell>

      <GridTableCell label="Último acesso" className="text-sm text-ui-navy-dark">
        {user.lastLoginAt ? formatDate(user.lastLoginAt, "DD/MM/YYYY [às] HH:mm") : "-"}
      </GridTableCell>

      <GridTableCell label="Criado em" className="text-sm text-ui-navy-dark">
        {formatDate(user.createdAt, "DD/MM/YYYY")}
      </GridTableCell>
    </Link>
  );
};

export default ManagerUsersTabListLine;
